import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import Modal from '@material-ui/core/Modal';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import LeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import RightIcon from '@material-ui/icons/KeyboardArrowRight';
import ProductImageGalleryThumbnails from '../ProductImageGalleryThumbnails';
import ProductImageDecorated from '../ProductImageDecorated';
var useStyles = makeStyles(function (theme) {
  return {
    root: {},
    content: _defineProperty({
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      position: 'fixed',
      minWidth: '80%',
      minHeight: '80vh',
      paddingLeft: theme.spacing(6),
      paddingRight: theme.spacing(6),
      paddingTop: theme.spacing(6),
      paddingBottom: theme.spacing(6),
      background: theme.palette.background.default,
      outline: 0
    }, theme.breakpoints.down('sm'), {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    }),
    button: {
      position: 'absolute',
      top: theme.spacing(0.5),
      right: theme.spacing(0.5)
    },
    activeImage: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexGrow: 1,
      alignSelf: 'center',
      flexBasis: '100%',
      height: '100%',
      flexShrink: 1
    },
    thumbnailActive: {
      boxShadow: theme.shadows[1]
    }
  };
}, {
  name: 'SxComProductImageModal'
});

var _ref2 = /*#__PURE__*/React.createElement(CloseIcon, null);

var _ref3 = /*#__PURE__*/React.createElement(LeftIcon, null);

var _ref4 = /*#__PURE__*/React.createElement(RightIcon, null);

function ProductImageModal(_ref) {
  var activeMediaIndex = _ref.activeMediaIndex,
      allMedia = _ref.allMedia,
      pClasses = _ref.classes,
      className = _ref.className,
      defaultAlt = _ref.defaultAlt,
      mediaTypeComponents = _ref.mediaTypeComponents,
      onClose = _ref.onClose,
      open = _ref.open,
      ProductImageDecoratedProps = _ref.ProductImageDecoratedProps,
      ProductImageThumnailsProps = _ref.ProductImageThumnailsProps,
      thumbnailModalSize = _ref.thumbnailModalSize,
      variant = _ref.variant,
      rest = _objectWithoutProperties(_ref, ["activeMediaIndex", "allMedia", "classes", "className", "defaultAlt", "mediaTypeComponents", "onClose", "open", "ProductImageDecoratedProps", "ProductImageThumnailsProps", "thumbnailModalSize", "variant"]);

  var _React$useState = React.useState(activeMediaIndex),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      index = _React$useState2[0],
      setIndex = _React$useState2[1];

  var classes = useStyles({
    classes: pClasses,
    active: index
  });

  var handleClose = function handleClose() {
    if (onClose) {
      onClose();
    }
  };

  var handleChangeIndex = React.useCallback(function (v) {
    return setIndex(v);
  }, []);
  var handleChange = React.useCallback(function (direction) {
    handleChangeIndex(function (state) {
      var nextIndex = state + direction;

      if (nextIndex < 0) {
        nextIndex = 0;
      } else if (nextIndex >= allMedia.length) {
        nextIndex = allMedia.length - 1;
      }

      return nextIndex;
    });
  }, [allMedia, handleChangeIndex]);
  React.useEffect(function () {
    return setIndex(activeMediaIndex);
  }, [activeMediaIndex]);
  var activeMedia = allMedia[index] || allMedia[0];
  return /*#__PURE__*/React.createElement(Modal, _extends({}, rest, {
    open: open,
    className: clsx(classes.root, className),
    onClose: handleClose
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.content
  }, /*#__PURE__*/React.createElement(IconButton, {
    className: classes.button,
    onClick: onClose
  }, _ref2), /*#__PURE__*/React.createElement("div", {
    className: classes.activeImage
  }, /*#__PURE__*/React.createElement(IconButton, {
    onClick: function onClick() {
      return handleChange(-1);
    },
    disabled: index === 0
  }, _ref3), /*#__PURE__*/React.createElement(ProductImageDecorated, _extends({
    size: 'big',
    active: true,
    alt: defaultAlt,
    src: activeMedia.src,
    stream: activeMedia.stream,
    type: activeMedia.type
  }, ProductImageDecoratedProps)), /*#__PURE__*/React.createElement(IconButton, {
    onClick: function onClick() {
      return handleChange(1);
    },
    disabled: index === allMedia.length - 1
  }, _ref4)), /*#__PURE__*/React.createElement(ProductImageGalleryThumbnails, _extends({
    showAllMedia: true,
    altDefault: defaultAlt,
    mediaTypeComponents: mediaTypeComponents,
    media: allMedia,
    variant: 'vertical',
    activeMedia: activeMedia,
    onChange: handleChangeIndex,
    classes: {
      thumbnailActive: classes.thumbnailActive
    },
    size: thumbnailModalSize
  }, ProductImageThumnailsProps))));
}

ProductImageModal.propTypes = {
  activeMediaIndex: PropTypes.number.isRequired,
  allMedia: PropTypes.arrayOf(PropTypes.shape({
    src: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string))]),
    stream: PropTypes.string,
    type: PropTypes.string
  })),
  classes: PropTypes.shape({
    activeImage: PropTypes.string,
    button: PropTypes.string,
    content: PropTypes.string,
    root: PropTypes.string,
    thumbnailActive: PropTypes.string,
    thumbnailsImages: PropTypes.string
  }),
  className: PropTypes.string,
  defaultAlt: PropTypes.arrayOf(PropTypes.shape({})),
  mediaTypeComponents: PropTypes.shape({}),
  onClose: PropTypes.func,
  open: PropTypes.bool,
  ProductImageDecoratedProps: PropTypes.shape({}),
  ProductImageThumnailsProps: PropTypes.shape({}),
  thumbnailModalSize: PropTypes.oneOf(['mini', 'small', 'normal', 'big']),
  variant: PropTypes.string
};
ProductImageModal.defaultProps = {
  allMedia: undefined,
  classes: undefined,
  className: undefined,
  defaultAlt: undefined,
  mediaTypeComponents: undefined,
  onClose: undefined,
  open: false,
  ProductImageDecoratedProps: undefined,
  ProductImageThumnailsProps: undefined,
  thumbnailModalSize: 'mini',
  variant: undefined
};
export default ProductImageModal;